import { Body, Headline } from "@bestowinc/kindred-ds/typography";
import styled from "@bestowinc/tinkertoy-shell-deps/styled-components";

export interface SupportedBrowser {
  name: string;
  minVersion: string;
}

/**
 * Browsers/versions Tinkertoy V2 supports. Sourced from the
 * [Browser Support Matrix in the V2 design doc](../../../../../../docs/tinkertoy-v2-design.md);
 * keep these two in sync when bumping support floors.
 *
 * Exported so tests can iterate over the same source of truth the
 * component renders, and so other host apps can reuse the list if they
 * need to render their own fallback.
 */
export const SUPPORTED_BROWSERS: ReadonlyArray<SupportedBrowser> = [
  { name: "Chrome", minVersion: "89+" },
  { name: "Edge", minVersion: "89+" },
  { name: "Firefox", minVersion: "108+" },
  { name: "Safari", minVersion: "16.4+" },
  { name: "iOS Safari", minVersion: "16.4+" },
  { name: "Chrome Android", minVersion: "89+" },
];

const Container = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing[24]};
  background: ${({ theme }) => theme.colors.background.default};
  text-align: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[16]};
  max-width: 480px;
  width: 100%;
`;

const BrowserList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
  text-align: left;
  width: 100%;
`;

const BrowserListItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[12]} ${theme.spacing[16]}`};
  background: ${({ theme }) => theme.colors.background.surface100};
  border-radius: ${({ theme }) => theme.borders.radius[4]};
`;

/**
 * Friendly fallback shown to users on browsers without ES Module support.
 *
 * Rendered by the legacy fallback bundle (see
 * `apps/tinkertoy-v2-shell/src/fallback-entry.tsx`), which is loaded via
 * `<script nomodule>` so it only runs when the modern shell can't. Lists
 * the supported browsers and tells the user to upgrade. No internal
 * feature names, no error details, no outbound links — pure user-facing
 * copy that we control.
 *
 * Renders its own `<main>` container; callers only need to wrap it in a
 * `ThemeProvider` (e.g. with `PURPLE_THEME`) so Kindred tokens resolve.
 */
export function BrowserNotSupported() {
  return (
    <Container data-testid="browser-not-supported" role="alert">
      <Content>
        <Headline as="h1" size="medium">
          This browser isn&rsquo;t supported
        </Headline>
        <Body as="p" size="medium">
          To continue, please upgrade to one of the supported browsers
          listed below.
        </Body>
        <BrowserList>
          {SUPPORTED_BROWSERS.map(({ name, minVersion }) => (
            <BrowserListItem key={name}>
              <Body as="span" size="medium">
                {name}
              </Body>
              <Body as="span" size="medium">
                {minVersion}
              </Body>
            </BrowserListItem>
          ))}
        </BrowserList>
      </Content>
    </Container>
  );
}
